<template>
  <div class="editor-wrapper">
    <div class="editor-toolbar">
      <button @click="editor.chain().focus().toggleHeading({ level: 1 }).run()" 
              :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }" 
              class="toolbar-button">
        <i class="bi bi-type-h1"></i>
      </button>
      <button @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" 
              :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }" 
              class="toolbar-button">
        <i class="bi bi-type-h2"></i>
      </button>
      <button @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" 
              :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }" 
              class="toolbar-button">
        <i class="bi bi-type-h3"></i>
      </button>
      <button @click="editor.chain().focus().toggleBold().run()" 
              :class="{ 'is-active': editor.isActive('bold') }" 
              class="toolbar-button">
        <i class="bi bi-type-bold"></i>
      </button>
      <button @click="editor.chain().focus().toggleItalic().run()" 
              :class="{ 'is-active': editor.isActive('italic') }" 
              class="toolbar-button">
        <i class="bi bi-type-italic"></i>
      </button>
      <button @click="editor.chain().focus().toggleBulletList().run()" 
              :class="{ 'is-active': editor.isActive('bulletList') }" 
              class="toolbar-button">
        <i class="bi bi-list-ul"></i>
      </button>
      <button @click="editor.chain().focus().toggleOrderedList().run()" 
              :class="{ 'is-active': editor.isActive('orderedList') }" 
              class="toolbar-button">
        <i class="bi bi-list-ol"></i>
      </button>
      <button @click="toggleAlphaList()" 
        :class="{ 'is-active': isActiveAlphaList }" 
        class="toolbar-button"
        :disabled="!canAlphaList"
        title="Sottolista alfabetica (disponibile solo dentro una lista)">
        <i class="bi bi-list-task"></i>
      </button>
      <slot>
      </slot>
      <input 
        type="file" 
        ref="fileInput" 
        @change="onFileChange" 
        accept="image/*" 
        style="display: none;"
      />
    </div>
    <editor-content :editor="editor" class="editor-content"/>
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-3';

export default {
  name: 'RichTextEditor',
  components: {
    EditorContent
  },
  data() {
    return {
      isActiveAlphaList: false,
      canAlphaList: false
    };
  },
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  mounted() {
    if (this.editor) {
      this.editor.on('selectionUpdate', this.updateAlphaState);
      this.editor.on('transaction', this.updateAlphaState);
      this.updateAlphaState();
    }
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.off('selectionUpdate', this.updateAlphaState);
      this.editor.off('transaction', this.updateAlphaState);
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    updateAlphaState() {
      if (!this.editor) return;
      const inList = this.editor.isActive('orderedList') || this.editor.isActive('bulletList') || this.editor.isActive('listItem');
      this.canAlphaList = !!inList;
      // Calcola attivo alpha in base al DOM
      this.$nextTick(() => {
        try {
          const { state, view } = this.editor;
          const pos = state.selection.$anchor.pos;
          const domAt = view.domAtPos(pos);
          let el = domAt.node;
          if (el && el.nodeType !== 1) el = el.parentNode;
          let ol = el;
          while (ol && ol.nodeName && ol.nodeName.toLowerCase() !== 'ol') {
            ol = ol.parentNode;
          }
          this.isActiveAlphaList = !!(ol && ol.getAttribute && ol.getAttribute('type') === 'a');
        } catch (e) {
          this.isActiveAlphaList = false;
        }
      });
    },
    toggleAlphaList() {
      if (!this.editor) return;
      if (!this.canAlphaList) return;

      const editor = this.editor;
      const inOrdered = editor.isActive('orderedList');
      const inBullet = editor.isActive('bulletList');

      // Focalizza
      editor.chain().focus();

      if (inBullet) {
        // Converti puntata -> numerica mantenendo gli item (fallback se toggleList non esiste)
        const chain = editor.chain();
        if (typeof chain.toggleList === 'function') {
          chain.toggleList('orderedList', 'listItem').run();
        } else {
          chain.toggleBulletList().toggleOrderedList().run();
        }
      } else if (!inOrdered) {
        // Non dovrebbe capitare (bottone attivo solo in lista), esci
        return;
      }

      // Implementazione della logica del TODO
      const ctx = this.getListDomContext();

      // 1) Se siamo già in una lista alfabetica (type="a"), aggiungi una nuova voce
      if (ctx.isAlpha) {
        if (editor.commands.splitListItem) {
          editor.commands.splitListItem('listItem');
        }
        // Stato visivo
        this.isActiveAlphaList = true;
        return;
      }

      // 2) Siamo in una orderedList normale
      if (!ctx.isFirst) {
        // Non è la prima voce: indenta la voce corrente (diventa sottolista) e poi imposta type="a" sulla sottolista
        let didSink = editor.commands.sinkListItem('listItem');
        if (!didSink && editor.commands.splitListItem) {
          editor.commands.splitListItem('listItem');
          didSink = editor.commands.sinkListItem('listItem');
        }
        // Post-render: imposta type="a" sulla <ol> annidata
        this.$nextTick(() => this.setAlphaOnCurrentNestedOl(5));
        // Prosegui a forzare anche via ProseMirror attrs se possibile (best effort)
      } else {
        // 3) È la prima voce: crea una sottolista annidata dividendo e poi indentando la nuova voce
        if (editor.commands.splitListItem) {
          editor.commands.splitListItem('listItem');
        }
        editor.commands.sinkListItem('listItem');
        // Post-render: imposta type="a" sulla <ol> annidata
        this.$nextTick(() => this.setAlphaOnCurrentNestedOl(5));
      }

      // Imposta type="a" sul nodo orderedList più interno nella selezione via transazione ProseMirror
      try {
        const { state, view } = this.editor;
        const { from, to } = state.selection;
        let targetPos = null;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.type && node.type.name === 'orderedList') {
            targetPos = pos; // prendi il più interno (ultimo visitato)
          }
        });
        if (targetPos !== null) {
          const listNode = state.doc.nodeAt(targetPos);
          const attrs = { ...(listNode?.attrs || {}), type: 'a' };
          const tr = state.tr.setNodeMarkup(targetPos, undefined, attrs);
          view.dispatch(tr);
          this.isActiveAlphaList = true;
        }
      } catch (_) {
        // ignore, fallback DOM
      }

      // Fallback DOM post-render
      this.$nextTick(() => this.setAlphaOnCurrentNestedOl(5));
    },
    getListDomContext() {
      try {
        const { state, view } = this.editor;
        const pos = state.selection.$anchor.pos;
        const domAt = view.domAtPos(pos);
        let el = domAt.node;
        if (el && el.nodeType !== 1) el = el.parentNode;
        // Trova LI corrente
        let li = el;
        while (li && li.nodeName && li.nodeName.toLowerCase() !== 'li') {
          li = li.parentNode;
        }
        // Trova OL contenitore
        let ol = li;
        while (ol && ol.nodeName && ol.nodeName.toLowerCase() !== 'ol') {
          ol = ol.parentNode;
        }
        const isAlpha = !!(ol && ol.getAttribute && ol.getAttribute('type') === 'a');
        let isFirst = false;
        if (li && ol && ol.children) {
          const items = Array.from(ol.children).filter(n => n.nodeName && n.nodeName.toLowerCase() === 'li');
          const index = items.indexOf(li);
          isFirst = index === 0;
        }
        const hasNext = !!(li && li.nextElementSibling && li.nextElementSibling.nodeName && li.nextElementSibling.nodeName.toLowerCase() === 'li');
        return { li, ol, isAlpha, isFirst, hasNext };
      } catch (e) {
        return { li: null, ol: null, isAlpha: false, isFirst: false, hasNext: false };
      }
    },
    setAlphaOnCurrentNestedOl(retries = 3) {
      try {
        const { state, view } = this.editor;
        const pos = state.selection.$anchor.pos;
        const domAt = view.domAtPos(pos);
        let el = domAt.node;
        if (el && el.nodeType !== 1) el = el.parentNode;
        // Trova l'LI corrente
        let li = el;
        while (li && li.nodeName && li.nodeName.toLowerCase() !== 'li') {
          li = li.parentNode;
        }
        // Cerca una <ol> figlia diretta della LI (sottolista) o l'antenato ol più vicino
        let target = null;
        if (li && li.querySelector) {
          target = li.querySelector(':scope > ol') || li.querySelector('ol');
        }
        if (!target) {
          target = li;
          while (target && target.nodeName && target.nodeName.toLowerCase() !== 'ol') {
            target = target.parentNode;
          }
        }
        if (target && typeof target.setAttribute === 'function') {
          target.setAttribute('type', 'a');
          this.isActiveAlphaList = true;
          return true;
        }
      } catch (e) {
        // ignore
      }
      if (retries > 0) {
        setTimeout(() => this.setAlphaOnCurrentNestedOl(retries - 1), 0);
      } else {
        this.isActiveAlphaList = false;
      }
      return false;
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Verifica che il file sia un'immagine
      if (!file.type.startsWith('image/')) {
        alert('Per favore seleziona un file immagine valido');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        // Torniamo al comando setImage che funzionava
        this.editor.chain().focus().setImage({
          src: result,
          alt: file.name,
          title: file.name
        }).run();
      };
      reader.readAsDataURL(file);
      
      // Reset dell'input file per consentire di selezionare la stessa immagine più volte
      event.target.value = '';
    }
  }
}
</script>

<style>
.editor-wrapper {
  border: 1px solid var(--bs-border-color, #ced4da);
  border-radius: 0.25rem;
}

.editor-toolbar {
  padding: 0.5rem;
  border-bottom: 1px solid var(--bs-border-color, #ced4da);
  background-color: var(--bs-tertiary-bg, #f8f9fa);
  display: flex;
  flex-wrap: wrap;
}

.toolbar-button {
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  margin-right: 0.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  color: var(--bs-body-color, #212529);
}

.toolbar-button:hover {
  background-color: var(--bs-secondary-bg, #e9ecef);
}

.toolbar-button.is-active {
  background-color: var(--bs-secondary-bg, #e2e6ea);
  color: var(--bs-primary, #0d6efd);
}

.toolbar-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.editor-content {
  padding: 1rem;
  min-height: 150px;
  color: var(--bs-body-color, #212529);
  background-color: var(--bs-body-bg, #fff);
}

/* Stili specifici per la dark mode */
[data-bs-theme="dark"] .editor-wrapper {
  border-color: var(--bs-border-color-translucent, #495057);
}

[data-bs-theme="dark"] .editor-toolbar {
  background-color: var(--bs-tertiary-bg, #343a40);
  border-color: var(--bs-border-color-translucent, #495057);
}

[data-bs-theme="dark"] .toolbar-button {
  color: var(--bs-body-color, #e9ecef);
}

[data-bs-theme="dark"] .toolbar-button:hover {
  background-color: var(--bs-secondary-bg, #495057);
}

[data-bs-theme="dark"] .toolbar-button.is-active {
  background-color: var(--bs-secondary-bg, #495057);
  color: var(--bs-primary, #6ea8fe);
}

/* Stili per le immagini ridimensionabili */
.ProseMirror img {
  max-width: 100%;
  height: auto;
  display: inline-block;
}

/* Liste alfabetiche se type="a"/"A" */
.ProseMirror ol[type="a"] {
  list-style-type: lower-alpha;
}
.ProseMirror ol[type="A"] {
  list-style-type: lower-alpha;
}

.resize-trigger {
  position: absolute;
  border: 1px solid #4285f4;
  background-color: white;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.resize-trigger-se {
  bottom: -4px;
  right: -4px;
  cursor: nwse-resize;
}

.resize-trigger-sw {
  bottom: -4px;
  left: -4px;
  cursor: nesw-resize;
}

.resize-trigger-ne {
  top: -4px;
  right: -4px;
  cursor: nesw-resize;
}

.resize-trigger-nw {
  top: -4px;
  left: -4px;
  cursor: nwse-resize;
}
/* Stili per le tabelle */
.ProseMirror table {
    border-collapse: collapse;
    margin: 1em 0;
    width: 100%;
    background-color: var(--bs-body-bg);
    box-shadow: 0 1px 3px rgba(var(--bs-body-color-rgb), 0.1);
}

.ProseMirror table td,
.ProseMirror table th {
    border: 1px solid var(--bs-border-color);
    padding: 0.75rem;
    text-align: left;
}

.ProseMirror table th {
    background-color: var(--bs-tertiary-bg);
    font-weight: 600;
}

.ProseMirror table tr:nth-child(even) {
    background-color: var(--bs-tertiary-bg);
}

.ProseMirror table tr:hover {
    background-color: var(--bs-secondary-bg);
}
</style>
